/**
 * Zod schema for `GET /api/jobs/:jobId/activity-feed`
 * (`apps/workers/src/api/jobs/activityFeed.ts`), consumed by
 * `@/lib/api/apiClient`'s `useActivityFeed()` hook.
 *
 * GitHub issue "P0: Customer churn — Tom (25 days, zero interviews,
 * requesting refund) — dashboard stale, pipeline broken". Backs the
 * customer activity feed panel on `@/pages/Dashboard.tsx` — a
 * chronological list of outreach-sent / email-opened / reply-received
 * events, so "1,100 candidates in pipeline / 100+ in outreach" is
 * actually visible to the customer instead of only ever being a number
 * Robert reads off an internal report.
 */

import { z } from 'zod';

export const activityFeedEventTypeSchema = z.enum([
  'outreach_sent',
  'email_opened',
  'reply_received',
]);
export type ActivityFeedEventType = z.infer<typeof activityFeedEventTypeSchema>;

export const activityFeedEventSchema = z.object({
  type: activityFeedEventTypeSchema,
  message: z.string(),
  timestamp: z.string(),
  messageId: z.string(),
  candidateEmail: z.string().nullable().optional().default(null),
});
export type ActivityFeedEvent = z.infer<typeof activityFeedEventSchema>;

export const activityFeedResponseSchema = z.object({
  jobId: z.string(),
  events: z.array(activityFeedEventSchema),
});
export type ActivityFeedResponse = z.infer<typeof activityFeedResponseSchema>;

/** Human-facing label per event type, for the feed panel's icon/badge. */
export const ACTIVITY_FEED_EVENT_LABELS: Record<ActivityFeedEventType, string> = {
  outreach_sent: 'Outreach sent',
  email_opened: 'Email opened',
  reply_received: 'Reply received',
};
